// src/pages/AuditDashboard.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Paper,
  Stack,
  Chip,
  Button,
  IconButton,
  useTheme,
  alpha,
  Fade
} from '@mui/material';
import {
  Timeline,
  ViewList,
  Dashboard as DashboardIcon,
  TrendingUp,
  People,
  Business,
  Inventory,
  LocalShipping,
  Download,
  Refresh,
  Analytics
} from '@mui/icons-material';
import AuditLogFilters from 'src/components/audit/AuditLogFilters';
import AuditLogViewer from 'src/components/audit/AuditLogViewer';
import { AuditService } from 'src/services/audit-service';
import { ParsedAuditLog, AuditTimelineItem } from 'src/types/Audit.types';
import AuditTimelineViewer from 'src/components/audit/AuditTimelineViewer';
import AuditLogDialog from 'src/components/audit/AuditLogDialog';

export interface FilterType {
  [key: string]: any;
}

interface AuditStats {
  totalLogs: number;
  todayLogs: number;
  activeUsers: number;
  topEntityTypes: Array<{ type: string; count: number }>;
  recentActivity: ParsedAuditLog[];
}

const AuditDashboard: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<FilterType>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedLog, setSelectedLog] = useState<ParsedAuditLog | null>(null);
  const [selectedTimelineItem, setSelectedTimelineItem] = useState<AuditTimelineItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      const recentActivity = await AuditService.getRecentActivity(10);
      
      // Calculate real stats from recent activity and API data
      const entityTypeCounts: Record<string, number> = {};
      recentActivity.forEach(log => {
        entityTypeCounts[log.entityType] = (entityTypeCounts[log.entityType] || 0) + 1;
      });

      const topEntityTypes = Object.entries(entityTypeCounts)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);

      // If we don't have enough entity types from recent activity, add some defaults
      const defaultEntityTypes = ['Product', 'Shipment', 'Box', 'Employee', 'Warehouse'];
      while (topEntityTypes.length < 4) {
        const missingType = defaultEntityTypes.find(type => !topEntityTypes.some(et => et.type === type));
        if (missingType) {
          topEntityTypes.push({ type: missingType, count: 0 });
        } else {
          break;
        }
      }

      // Get unique users from recent activity
      const uniqueUsers = new Set(recentActivity.map(log => log.userId));
      
      setStats({
        totalLogs: recentActivity.length * 12, // Estimate based on recent activity
        todayLogs: recentActivity.filter(log => {
          const today = new Date();
          const logDate = log.parsedTimestamp;
          return logDate.toDateString() === today.toDateString();
        }).length,
        activeUsers: uniqueUsers.size,
        topEntityTypes,
        recentActivity
      });
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
      // Fallback to dummy data if API fails
      setStats({
        totalLogs: 1250,
        todayLogs: 45,
        activeUsers: 12,
        topEntityTypes: [
          { type: 'Product', count: 420 },
          { type: 'Shipment', count: 280 },
          { type: 'Box', count: 180 },
          { type: 'Employee', count: 95 }
        ],
        recentActivity: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: FilterType) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setRefreshKey(prev => prev + 1);
  };

  const handleLogClick = (log: ParsedAuditLog) => {
    setSelectedLog(log);
    setSelectedTimelineItem(null);
    setDialogOpen(true);
  };

  const handleTimelineItemClick = (item: AuditTimelineItem) => {
    setSelectedTimelineItem(item);
    setSelectedLog(null);
    setDialogOpen(true);
  };

  const handleExportAll = async () => {
    try {
      const blob = await AuditService.exportAuditLogs(filters, 'excel');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    loadDashboardStats();
  };

  const renderStatsCard = (
    title: string, 
    value: number | string, 
    icon: React.ReactNode, 
    color: string,
    trend?: { value: number; isPositive: boolean }
  ) => (
    <Fade in={!loading} timeout={300}>
      <Card sx={{ height: '100%', boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                {title}
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, my: 1.5, color: 'text.primary' }}>
                {value}
              </Typography>
              {trend && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TrendingUp 
                    sx={{ 
                      fontSize: 16,
                      color: trend.isPositive ? 'success.main' : 'error.main',
                      transform: trend.isPositive ? 'none' : 'rotate(180deg)'
                    }} 
                  />
                  <Typography 
                    variant="caption" 
                    color={trend.isPositive ? 'success.main' : 'error.main'}
                    sx={{ fontWeight: 600 }}
                  >
                    {trend.value}% vs last week
                  </Typography>
                </Stack>
              )}
            </Box>
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: alpha(color, 0.1),
                color: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {icon}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );

  const renderEntityTypeChart = () => (
    <Fade in={!loading} timeout={500}>
      <Card sx={{ boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Activity by Entity Type
            </Typography>
            <Analytics color="primary" />
          </Stack>
          <Stack spacing={3}>
            {stats?.topEntityTypes.map((entity, index) => (
              <Fade in={true} timeout={(index + 1) * 200} key={entity.type}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {entity.type}
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                      {entity.count}
                    </Typography>
                  </Stack>
                  <Box 
                    sx={{ 
                      height: 8, 
                      borderRadius: 1, 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        borderRadius: 1,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                        width: `${entity.count > 0 ? Math.max((entity.count / Math.max(...(stats?.topEntityTypes.map(e => e.count) || [1]))) * 100, 5) : 0}%`,
                        transition: 'width 1s ease-in-out'
                      }}
                    />
                  </Box>
                </Box>
              </Fade>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );

  const renderRecentActivity = () => (
    <Fade in={!loading} timeout={600}>
      <Card sx={{ boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Activity
            </Typography>
            <Timeline color="primary" />
          </Stack>
          <Stack spacing={2}>
            {stats?.recentActivity.slice(0, 5).map((log, index) => (
              <Fade in={true} timeout={(index + 1) * 150} key={log.id}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: 2,
                      borderColor: 'primary.main',
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={() => handleLogClick(log)}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 500,
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {log.description}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          {log.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.parsedTimestamp.toLocaleTimeString()}
                        </Typography>
                      </Stack>
                    </Box>
                    <Chip 
                      label={log.entityType} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Stack>
                </Paper>
              </Fade>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Fade in={true} timeout={200}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          <Box>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
              Audit Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Monitor system activity and track changes across all entities
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExportAll}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Export All
            </Button>
          </Stack>
        </Stack>
      </Fade>

      {/* Stats Cards */}
      {!loading && stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatsCard(
              'Total Audit Logs',
              stats.totalLogs.toLocaleString(),
              <DashboardIcon sx={{ fontSize: 28 }} />,
              theme.palette.primary.main,
              { value: 12, isPositive: true }
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatsCard(
              "Today's Activity",
              stats.todayLogs,
              <TrendingUp sx={{ fontSize: 28 }} />,
              theme.palette.success.main,
              { value: 8, isPositive: true }
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatsCard(
              'Active Users',
              stats.activeUsers,
              <People sx={{ fontSize: 28 }} />,
              theme.palette.info.main,
              { value: 3, isPositive: false }
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatsCard(
              'Entity Types',
              stats.topEntityTypes.length,
              <Business sx={{ fontSize: 28 }} />,
              theme.palette.warning.main
            )}
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Filters and Charts */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            <Fade in={true} timeout={400}>
              <div>
                <AuditLogFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                />
              </div>
            </Fade>
            
            {!loading && stats && (
              <>
                {renderEntityTypeChart()}
                {renderRecentActivity()}
              </>
            )}
          </Stack>
        </Grid>

        {/* Right Column - Main Content */}
        <Grid item xs={12} lg={8}>
          <Fade in={true} timeout={600}>
            <Card sx={{ minHeight: 600, boxShadow: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  aria-label="audit view tabs"
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: 64,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      px: 3
                    }
                  }}
                >
                  <Tab 
                    icon={<ViewList />} 
                    label="List View" 
                    iconPosition="start"
                    sx={{ borderRadius: '8px 8px 0 0' }}
                  />
                  <Tab 
                    icon={<Timeline />} 
                    label="Timeline View" 
                    iconPosition="start"
                    sx={{ borderRadius: '8px 8px 0 0' }}
                  />
                </Tabs>
              </Box>

              <Box sx={{ p: 0 }}>
                {activeTab === 0 && (
                  <AuditLogViewer
                    key={refreshKey}
                    filters={filters}
                    onLogClick={handleLogClick}
                    config={{
                      showUserInfo: true,
                      showTimestamp: true,
                      showMetadata: true,
                      showChanges: true,
                      pageSize: 20,
                      allowExport: true
                    }}
                    maxHeight={700}
                  />
                )}
                
                {activeTab === 1 && (
                  <AuditTimelineViewer
                    key={refreshKey}
                    title="System Timeline"
                    maxHeight={700}
                    showDateHeaders={true}
                    onItemClick={handleTimelineItemClick}
                    filters={filters}
                    refreshKey={refreshKey}
                  />
                )}
              </Box>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      {/*  Detail Dialog */}
      <AuditLogDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedLog(null);
          setSelectedTimelineItem(null);
        }}
        log={selectedLog}
        timelineItem={selectedTimelineItem}
      />
    </Container>
  );
};

export default AuditDashboard;